/**
 * Bilingual description formatter for Journal Entry details.
 *
 * Strategy (safe, conservative):
 *  1) Empty description → fallback to account name in current UI language.
 *  2) Translate known prefix phrases (e.g. "صرف داخلي - " ↔ "Internal Issue - ")
 *     using startsWith — never replaces in the middle of a string.
 *  3) Swap accountNameAr ↔ accountName if it appears anywhere in the description
 *     (this handles cases like "صرف داخلي - مصروفات الوقود للادارة" where the
 *     Arabic account name is embedded).
 *  4) Otherwise return the description AS-IS (no risk of data corruption).
 */

// Sorted by length DESC so longer/more specific prefixes match first.
const AR_TO_EN_PREFIXES: Array<[string, string]> = [
  ['مصدر الدفع - فاتورة شراء ', 'Payment Source - Purchase Invoice '],
  ['تخفيض مخزون المخزن - ', 'Warehouse Stock Decrease - '],
  ['زيادة مخزون المخزن - ', 'Warehouse Stock Increase - '],
  ['مخزون - فاتورة شراء ', 'Inventory - Purchase Invoice '],
  ['تسوية مخزون الفرع - ', 'Branch Stock Adjustment - '],
  ['استرداد متبقي عُهدة ', 'Custody Refund - '],
  ['كشف تسوية رقم ', 'Settlement Report No. '],
  ['مصروف تسوية عُهدة', 'Custody Settlement Expense'],
  ['صرف داخلي - ', 'Internal Issue - '],
  ['تسوية عُهدة ', 'Custody Settlement - '],
  ['عجز مخزون - ', 'Stock Shortage - '],
  ['فائض مخزون - ', 'Stock Surplus - '],
  ['أصل ثابت', 'Fixed Asset'],
  ['مخزن ', 'Warehouse '],
];

const EN_TO_AR_PREFIXES: Array<[string, string]> = [
  ['Payment to Supplier - PI ', 'دفع للمورد - فاتورة '],
  ['Payment from Custody - PI ', 'دفع من العُهدة - فاتورة '],
  ['Custody Warehouse Delivery - ', 'تسليم عُهدة للمخزن - '],
  ['Custody Branch Delivery - ', 'تسليم عُهدة للفرع - '],
  ['Inventory - Purchase Invoice ', 'مخزون - فاتورة شراء '],
  ['Custody - Purchase Invoice ', 'عُهدة - فاتورة شراء '],
  ['Branch Transfer From - ', 'تحويل فرع من - '],
  ['Branch Transfer To - ', 'تحويل فرع إلى - '],
  ['Warehouse Inventory - ', 'مخزون المخزن - '],
  ['Inventory Opening - ', 'افتتاحي المخزون - '],
  ['Stock Adjustment - ', 'تسوية مخزون - '],
  ['Branch Transfer - ', 'تحويل فرع - '],
  ['Branch Inventory - ', 'مخزون الفرع - '],
  ['Custody Settlement #', 'تسوية عُهدة #'],
  ['Internal Issue - ', 'صرف داخلي - '],
  ['Purchase Return - ', 'مرتجع شراء - '],
  ['Purchase Invoice ', 'فاتورة شراء '],
  ['Stock Receipt - ', 'استلام مخزون - '],
  ['Stock Issue - ', 'صرف مخزون - '],
  ['Daily Sales - ', 'مبيعات يومية - '],
  ['Branch Issue ', 'صرف فرع '],
  ['Transfer: ', 'تحويل: '],
  ['Custody - ', 'عُهدة - '],
  ['Sales - ', 'مبيعات - '],
];

export const formatJEDescription = (
  description: string | undefined | null,
  isRTL: boolean,
  accountName?: string | null,
  accountNameAr?: string | null
): string => {
  const desc = (description || '').trim();

  // Case 1: empty → fallback to account name in current language
  if (!desc) {
    if (isRTL) return accountNameAr || accountName || '-';
    return accountName || accountNameAr || '-';
  }

  let result = desc;

  if (!isRTL) {
    // ── English UI ──
    // 1) Translate Arabic prefix at start of string (first match wins)
    for (const [ar, en] of AR_TO_EN_PREFIXES) {
      if (result.startsWith(ar)) {
        result = en + result.slice(ar.length);
        break;
      }
    }
    // 2) Replace accountNameAr substring with accountName if present
    const ar = (accountNameAr || '').trim();
    const en = (accountName || '').trim();
    if (ar && en && result.includes(ar)) {
      result = result.split(ar).join(en);
    }
  } else {
    // ── Arabic UI ──
    for (const [en, ar] of EN_TO_AR_PREFIXES) {
      if (result.startsWith(en)) {
        result = ar + result.slice(en.length);
        break;
      }
    }
    const ar = (accountNameAr || '').trim();
    const en = (accountName || '').trim();
    if (en && ar && result.includes(en)) {
      result = result.split(en).join(ar);
    }
  }

  return result;
};
